import Link from "@mui/material/Link";
import { styled } from '@mui/material/styles';
import { useDialog } from "app/providers/dialog";
import Loader from "common/components/loader";
import _ from "lodash";
import Individual from "marketplace/entities/individual/individual";
import { MouseEvent } from "react";
import Session from "users/session/session";
import ProposalAPIService from "work/entities/proposal/api/proposal-api-service";
import DetailsTab from "work/entities/proposal/draft/view/tabs/details-tab";
import Proposal from "work/entities/proposal/proposal";
import WorkAgreementAPIService from "work/entities/work-agreement/api/work-agreement-api-service";
import { VendorTeamMember } from "work/values/vendor-team/vendor-team-member";
import ViewVendorTeam from "work/values/vendor-team/view/view-vendor-team";
import { Masked } from "work/entities/work-agreement/work-agreement";

const StyledLink = styled(
  Link,
  { shouldForwardProp: prop => prop !== 'revised' }
)<{ revised: boolean }>(
  ({ theme, revised }) => ({
    alignItems: 'center',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    display: 'flex',
    overflow: 'hidden',
    textDecoration: revised ? 'underline dotted red' : 'underline',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }));

type VendorTeamLinkProps = {
  proposal: Proposal;
  session: Readonly<Session>;
};

export default function VendorTeamLink(props: Readonly<VendorTeamLinkProps>) {
  const {
    proposal,
    session
  } = props;

  const { openDialog, closeDialog } = useDialog();

  async function getDetailedProposal(): Promise<Proposal | undefined> {
    if (!proposal.id) {
      console.error(new Error('Proposal ID is required'));
      return;
    }

    try {
      const workAgreementApiService = new WorkAgreementAPIService(session);
      const proposalService = new ProposalAPIService(session, workAgreementApiService);
      const { proposal: detailedProposal } = await proposalService.getProposalById(proposal.id);
      return detailedProposal;
    } catch (error) {
      console.error(error);
    }
  }

  async function handleClick(event: MouseEvent) {
    if (!proposal?.vendorTeam || Masked.isMasked(proposal.vendorTeam)) return;

    event.stopPropagation();

    openDialog({
      title: 'Team Members',
      component: <Loader />
    });

    const detailedProposal = await getDetailedProposal();
    if (!detailedProposal || Masked.isMasked(detailedProposal.details?.vendorTeam)) return;

    const leaderIndividual = detailedProposal?.details?.vendorTeam?.leader;
    if (!leaderIndividual) throw new Error("Leader is required");

    const memberIndividuals = detailedProposal?.details?.vendorTeam?.members;

    const leader: VendorTeamMember = {
      individualId: leaderIndividual.id,
      userId: leaderIndividual.userId ?? undefined,
      entityId: leaderIndividual.company?.entityId,
      firstName: leaderIndividual.profile?.firstName ?? '',
      lastName: leaderIndividual.profile?.lastName ?? '',
      avatarId: leaderIndividual.profile?.avatarId
    };

    const members: VendorTeamMember[] = memberIndividuals?.map((member: Individual) => ({
      individualId: member.id,
      userId: member.userId ?? undefined,
      entityId: member.company?.entityId,
      firstName: member.profile?.firstName ?? '',
      lastName: member.profile?.lastName ?? '',
      avatarId: member.profile?.avatarId
    })) ?? [];

    closeDialog();

    openDialog({
      title: `${DetailsTab.name.toString()} Team`,
      MuiProps: { maxWidth: 'md' },
      component: (
        <ViewVendorTeam
          leader={leader}
          members={members}
        />
      )
    });
  }

  return (
    <StyledLink
      revised={
        proposal?.supersedes !== null &&
        !_.isEqual(
          proposal?.vendorTeam,
          proposal?.supersedes?.vendorTeam
        )
      }
      onClick={handleClick}>
      View
    </StyledLink>
  );
}
